import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Application from 'expo-application';
import * as Crypto from 'expo-crypto';
import { Platform } from 'react-native';

import { requestIntegrityToken } from '../../modules/play-integrity';

/**
 * Krown install-attribution client — closes the click→install loop.
 *
 * Tracked links mint `utm_content=<token>` into the Play Store URL. On first
 * launch Android hands the app that referrer via the Play Install Referrer API;
 * we ping Krown, which maps `token → post → app`. The ping is attested with a
 * Play Integrity token bound to a nonce the server recomputes byte-for-byte.
 *
 * Fires exactly once, guarded by a persisted flag. On network failure the flag
 * stays unset so the next launch retries.
 */

const SENT_KEY = 'krown_install_referrer_sent';
const ENDPOINT = 'https://krownmedia.online/api/v2/growth/install-referrer';

const INGEST_KEY = process.env.EXPO_PUBLIC_KROWN_INGEST_KEY ?? '';
// Optional: only needed when the app is not distributed through the linked
// Play Console project. Parsed as a number for the Play Integrity request.
const CLOUD_PROJECT_NUMBER = Number(process.env.EXPO_PUBLIC_KROWN_CLOUD_PROJECT_NUMBER) || undefined;

/** Convert a base64 string to base64url (server recomputes the nonce this way). */
function toBase64Url(base64: string): string {
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Read the raw Play Install Referrer string (resolves '' if unavailable).
 *
 * The module is require'd HERE rather than imported at the top of the file.
 * react-native-play-install-referrer is CommonJS and Android-only; a static import
 * makes webpack parse it for the web bundle too, which fails with "ES Modules may
 * not assign module.exports" and takes the whole web app down before it mounts —
 * breaking the screenshot harness and the App Store screenshot pipeline with it.
 * A runtime Platform check cannot prevent that, because the failure is at bundle
 * parse time. Callers already gate on Platform.OS === 'android'.
 */
function getInstallReferrer(): Promise<string> {
  return new Promise((resolve, reject) => {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { PlayInstallReferrer } = require('react-native-play-install-referrer');
    PlayInstallReferrer.getInstallReferrerInfo((
      info: { installReferrer?: string } | null,
      error: { message?: string } | string | null,
    ) => {
      if (error) {
        reject(new Error(typeof error === 'string' ? error : (error?.message ?? 'referrer error')));
      } else {
        resolve(info?.installReferrer ?? '');
      }
    });
  });
}

/**
 * Report this install to Krown exactly once. Safe to call on every launch and
 * on every platform — it no-ops on non-Android and after a successful send.
 */
export async function reportInstallOnce(): Promise<void> {
  try {
    // Android-only: the Install Referrer + Play Integrity APIs are Play-specific.
    if (Platform.OS !== 'android') return;
    // Without an ingest key the ping cannot succeed — skip without burning the flag.
    if (!INGEST_KEY) return;
    // Already reported.
    if (await AsyncStorage.getItem(SENT_KEY)) return;

    const referrer = await getInstallReferrer();

    // Not one of our tracked links → mark done so we never look again.
    if (!referrer.includes('utm_source=krown')) {
      await AsyncStorage.setItem(SENT_KEY, '1');
      return;
    }

    const packageName = Application.applicationId ?? 'kingdom.bible.quiz';
    const installId = Crypto.randomUUID();
    const installedAt = Date.now();

    // nonce = base64url(sha256("packageName|installId|referrer|installedAt"))
    const nonceB64 = await Crypto.digestStringAsync(
      Crypto.CryptoDigestAlgorithm.SHA256,
      `${packageName}|${installId}|${referrer}|${installedAt}`,
      { encoding: Crypto.CryptoEncoding.BASE64 }
    );
    const nonce = toBase64Url(nonceB64);

    const integrityToken = await requestIntegrityToken(nonce, CLOUD_PROJECT_NUMBER);

    const response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-krown-ingest-key': INGEST_KEY,
      },
      body: JSON.stringify({
        packageName,
        platform: 'android',
        referrer,
        installId,
        installedAt,
        integrityToken,
      }),
    });

    if (response.ok) {
      await AsyncStorage.setItem(SENT_KEY, '1');
    }
    // else: leave the flag unset so the next launch retries.
  } catch (error) {
    // Never let attribution crash app startup; retry on the next launch.
    if (__DEV__) {
      // eslint-disable-next-line no-console
      console.warn('[installAttribution] reportInstallOnce failed:', error);
    }
  }
}
